import type { FeatureFlagKey } from "~/account/constants/feature-flags";
import type { IUser } from "~/types";

function getLocalStorageItem<T>(key: string): T | null {
  const item = localStorage.getItem(key);
  return item ? (JSON.parse(item) as T) : null;
}

export function isSuperAdmin() {
  const user: IUser | null = getLocalStorageItem("user");

  return user?.hasSuperAdminRole;
}

export function isFeatureEnabled(name: FeatureFlagKey): boolean {
  const user: IUser | null = getLocalStorageItem("user");

  const appConfig = useAppConfig();
  const featureValue = appConfig.features[name];

  if (typeof featureValue === "boolean") {
    return featureValue;
  }

  if (typeof featureValue !== "object") {
    return false;
  }

  if (featureValue.superAdmins && isSuperAdmin()) {
    return true;
  }

  if (featureValue?.users?.length) {
    return featureValue.users.includes(user?.email ?? "");
  }

  return false;
}

export const canViewLoggingProfiles = () =>
  !!isSuperAdmin() && (isFeatureEnabled("enable_view_logging_profiles") || isFeatureEnabled("enable_edit_logging_profiles"));

export const canEditLoggingProfiles = () => !!isSuperAdmin() && isFeatureEnabled("enable_edit_logging_profiles");

export const canAssignLoggingProfiles = () => !!isSuperAdmin() && isFeatureEnabled("enable_assign_logging_profiles");

export const canViewLoggingJobs = () => !!isSuperAdmin() && isFeatureEnabled("enable_view_logging_jobs");

export const canViewBreakdownRequests = () => !!isSuperAdmin() && isFeatureEnabled("enable_view_breakdown_requests");

export const canSubmitLoggingJobs = () => !!isSuperAdmin() && isFeatureEnabled("enable_submit_logging_jobs");

export const canViewLoggingRules = () => !!isSuperAdmin() && (isFeatureEnabled("enable_assign_logging_rules") || isFeatureEnabled("enable_view_logging_rules"));

export const canAssignLoggingRules = () => !!isSuperAdmin() && isFeatureEnabled("enable_assign_logging_rules");

export const canEditBreakdownBilling = () => !!isSuperAdmin() && isFeatureEnabled("enable_edit_breakdown_billing");
